import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import MasterLocationSelect from "components/MasterLocationSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_URL } from "config";
import { useAuth } from "../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";
import { creditNoteActionPermission, creditNoteFormFieldPermission } from "./creditNotePermissions";
import { MANUAL_CREDIT_OPTION, normalizeManualReference, validateCreditNoteDraft, creditNoteSourcePayload, manualCreditNoteAmounts, roundManualCreditMoney } from "./manualCreditNote";

const emptyAddress = {
  name: "",
  email: "",
  address: "",
  state: "",
  state_id: null,
  postal_code: "",
  country: "",
  country_id: null,
};
const addressFields = [
  ["name", "Name / Company"],
  ["email", "Email"],
  ["address", "Address"],
  ["state", "State"],
  ["postal_code", "Pincode"],
  ["country", "Country"],
];

const localToday = () => {
  const date = new Date();
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const round = (value, digits = 2) => Number(Number(value || 0).toFixed(digits));
const money = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const optionLabel = (invoice) => {
  const remaining = money(invoice?.remaining_amount_usd);
  return `${invoice?.invoice_number || "Invoice"} — ${invoice?.client_name || "Client"} — $${remaining} remaining`;
};
const asOption = (invoice) => ({ ...invoice, value: invoice.id, label: optionLabel(invoice) });

const cellSx = {
  px: 0.55,
  py: 0.55,
  fontSize: "11px",
  "& .MuiInputBase-root": { minHeight: "34px", height: "34px", fontSize: "12px" },
};
const headSx = { px: 0.55, py: 0.7, fontSize: "11px", lineHeight: 1.2 };

function CreditNoteForm({ mode }) {
  const isEdit = mode === "edit";
  const { id } = useParams();
  const navigate = useNavigate();
  const { permissions = [], userPermission = [], user, accessScopes } = useAuth();
  const { hasModulePermission } = useModulePermissions(permissions, userPermission, "credit_notes");
  const canField = (field) => hasModulePermission(creditNoteFormFieldPermission(mode, field));
  const canSave = hasModulePermission(creditNoteActionPermission(isEdit ? "update" : "create"));

  const authToken = localStorage.getItem("authToken");
  const actorKey = JSON.stringify([user?.id, authToken, permissions, userPermission, accessScopes]);
  const headers = useMemo(() => ({
    Authorization: `Bearer ${authToken}`,
    Accept: "application/json",
  }), [authToken]);
  const requestVersion = useRef(0);
  const sourceAbort = useRef(null);
  const saveAbort = useRef(null);
  const savingRef = useRef(false);
  const actorRef = useRef(actorKey);
  actorRef.current = actorKey;
  const sourceModeRef = useRef("invoice");
  const rowSequence = useRef(0);
  const [loadedActorKey, setLoadedActorKey] = useState("");
  const [sourceType, setSourceType] = useState("invoice");
  const [manualReference, setManualReference] = useState("");
  const [manualClients, setManualClients] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [allOptions, setAllOptions] = useState([]);
  const isManual = sourceType === "manual";
  const canCreateManual = ["source_invoice_numbers", "client", "invoicing_company", "items_section", "item_description", "item_quantity", "item_rate"].every(canField);

  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [client, setClient] = useState(null);
  const [company, setCompany] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    credit_note_date: localToday(),
    reference_number: "",
    notes: "",
    shipping_same_as_billing: true,
    billing: { ...emptyAddress },
    shipping: { ...emptyAddress },
  });
  const [number, setNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, color: "success", message: "" });
  const [minimumDate, setMinimumDate] = useState("");
  const [maximumDate, setMaximumDate] = useState(localToday());

  const request = async (path, init = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: { ...headers, ...(init.headers || {}) },
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const validation = result.errors ? Object.values(result.errors).flat()[0] : null;
      throw new Error(validation || result.message || "Unable to process credit note request.");
    }
    return result;
  };

  const setSnapshotAddresses = (data, existing = null) => {
    const source = existing || data || {};
    setForm((current) => ({
      ...current,
      reference_number: source.reference_number ?? current.reference_number,
      notes: source.notes ?? current.notes,
      shipping_same_as_billing: Boolean(source.shipping_same_as_billing ?? true),
      billing: {
        name: source.billing_name || "",
        email: source.billing_email || "",
        address: source.billing_address || "",
        state: source.billing_state || "",
        state_id: source.billing_state_id || null,
        postal_code: source.billing_postal_code || "",
        country: source.billing_country || "",
        country_id: source.billing_country_id || null,
      },
      shipping: {
        name: source.shipping_name || "",
        email: source.shipping_email || "",
        address: source.shipping_address || "",
        state: source.shipping_state || "",
        state_id: source.shipping_state_id || null,
        postal_code: source.shipping_postal_code || "",
        country: source.shipping_country || "",
        country_id: source.shipping_country_id || null,
      },
    }));
  };

  const buildItems = (sourceItems, existingItems = null) => (sourceItems || [])
    .filter((item) => Number(item.remaining_total) > 0 && Number(item.remaining_quantity) > 0)
    .map((item) => {
      const existing = (existingItems || []).find(
        (row) => Number(row.source_invoice_item_id) === Number(item.source_invoice_item_id)
      );
      const rate = existing ? Number(existing.rate) : Number(item.rate || 0);
      const total = existing ? Number(existing.total) : Number(item.remaining_total || 0);
      const quantity = existing
        ? Number(existing.quantity)
        : Number(item.remaining_quantity || 0);
      return {
        ...item,
        description: existing?.description ?? item.description ?? "",
        quantity,
        rate,
        total: round(total),
      };
    });

  const applySourceDetails = (data, existing = null) => {
    const invoiceMap = new Map();
    [...(data.available_invoices || []), ...(data.source_invoices || [])].forEach((invoice) => {
      invoiceMap.set(Number(invoice.id), asOption(invoice));
    });
    const sourceOptions = (data.source_invoices || []).map((invoice) => invoiceMap.get(Number(invoice.id)));
    setOptions(Array.from(invoiceMap.values()));
    setSelected(sourceOptions.filter(Boolean));
    setClient(data.client || null);
    setCompany(data.invoicing_company || null);
    setSnapshot(data.snapshot || null);
    setSnapshotAddresses(data.snapshot, existing);
    setItems(buildItems(data.items, existing?.items));
  };

  const stopSourceRequest = () => {
    requestVersion.current += 1;
    sourceAbort.current?.abort();
    sourceAbort.current = null;
    setSourceLoading(false);
  };

  const resetSource = () => {
    setSelected([]);
    setClient(null);
    setCompany(null);
    setSnapshot(null);
    setItems([]);
    setManualReference("");
    setError("");
    setForm((current) => ({ ...current, reference_number: "", shipping_same_as_billing: true,
      billing: { ...emptyAddress }, shipping: { ...emptyAddress } }));
  };

  const loadSourceDetails = async (invoiceIds, existing = null) => {
    if (!invoiceIds.length || sourceModeRef.current !== "invoice") return;
    stopSourceRequest();
    const controller = new AbortController();
    sourceAbort.current = controller;
    const version = requestVersion.current;
    const actor = actorKey;
    setSourceLoading(true);
    setError("");
    try {
      const result = await request("/credit-notes/source-details", {
        method: "POST",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          invoice_ids: invoiceIds,
          ...(isEdit && id ? { credit_note_id: Number(id) } : {}),
        }),
      });
      if (controller.signal.aborted || version !== requestVersion.current || actor !== actorRef.current
        || sourceModeRef.current !== "invoice") return;
      applySourceDetails(result, existing);
    } catch (requestError) {
      if (controller.signal.aborted || version !== requestVersion.current || actor !== actorRef.current) return;
      setError(requestError.message);
      setSnackbar({ open: true, color: "error", message: requestError.message });
    } finally {
      if (version === requestVersion.current && actor === actorRef.current) setSourceLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    stopSourceRequest();
    saveAbort.current?.abort();
    savingRef.current = false;
    setSaving(false);
    sourceModeRef.current = "invoice";
    setSourceType("invoice");
    resetSource();
    setNumber("");
    setOptions([]);
    setAllOptions([]);
    setManualClients([]);
    setCurrencies([]);
    const load = async () => {
      setLoading(true);
      try {
        const optionResult = await request(`/credit-notes/form-options?mode=${mode}`, { signal: controller.signal });
        if (!active || controller.signal.aborted) return;
        const invoiceOptions = (optionResult.invoices || []).map(asOption);
        setOptions(invoiceOptions);
        setAllOptions(invoiceOptions);
        setManualClients(optionResult.manual_clients || []);
        setCurrencies(optionResult.currencies || []);
        setMinimumDate(optionResult.minimum_credit_note_date || "");
        setMaximumDate(optionResult.maximum_credit_note_date || optionResult.today || localToday());
        if (isEdit) {
          const noteResult = await request(`/credit-notes/${id}?mode=edit`, { signal: controller.signal });
          if (!active || controller.signal.aborted) return;
          const note = noteResult.data || noteResult;
          setNumber(note.credit_note_number || note.invoice_number || "");
          setForm((current) => ({ ...current,
            credit_note_date: note.credit_note_date || note.invoice_date || current.credit_note_date,
            reference_number: note.reference_number || "", notes: note.notes || "",
          }));
          if (note.source_type === "manual") {
            sourceModeRef.current = "manual";
            setSourceType("manual");
            setManualReference(note.manual_reference || "");
            const savedClient = (optionResult.manual_clients || []).find((entry) => Number(entry.id || entry.value) === Number(note.client_id));
            setClient(savedClient?.client || { id: note.client_id, name: note.party?.name || note.billing_name || "Client" });
            setCompany(note.invoicing_company ? { ...note.invoicing_company,
              id: note.invoicing_company.id || note.invoicing_company_id,
              name: note.invoicing_company.name || note.invoicing_company.company_name,
            } : savedClient?.invoicing_company || { id: note.invoicing_company_id });
            setSnapshot({ ...(savedClient?.snapshot || {}), ...note,
              tax_percentage: note.manual_tax_rule?.percentage ?? savedClient?.snapshot?.tax_percentage ?? note.tax_percentage,
              tax_rule: note.manual_tax_rule?.rule ?? savedClient?.snapshot?.tax_rule ?? note.tax_rule,
            });
            setSnapshotAddresses(note, note);
            setItems((note.items || []).map((item) => ({ ...item, row_key: `saved-${item.id}` })));
          } else await loadSourceDetails((note.source_invoice_ids || []).map(Number), note);
        }
      } catch (requestError) {
        if (!active || controller.signal.aborted) return;
        setError(requestError.message);
      } finally {
        if (active) { setLoadedActorKey(actorKey); setLoading(false); }
      }
    };
    load();
    return () => {
      active = false;
      controller.abort();
      sourceAbort.current?.abort();
      requestVersion.current += 1;
      saveAbort.current?.abort();
    };
  }, [id, isEdit, mode, actorKey]);

  const chooseInvoices = async (values, action = {}) => {
    if (savingRef.current || !canField("source_invoice_numbers")) return;
    const next = values || [];
    const nextMode = action.option?.value === "manual" ? "manual"
      : action.option && action.option.value !== "manual" ? "invoice"
        : next.some((option) => option.value === "manual") ? "manual" : "invoice";
    if (nextMode !== sourceModeRef.current) {
      if (isEdit || (nextMode === "manual" && !canCreateManual)) return;
      if ((items.length || client || manualReference) && !window.confirm("Changing the credit note source clears the selected client, addresses and credit rows. Continue?")) return;
      stopSourceRequest();
      resetSource();
      sourceModeRef.current = nextMode;
      setSourceType(nextMode);
      setOptions(allOptions);
      if (nextMode === "manual") {
        rowSequence.current += 1;
        setItems([{ row_key: `manual-${rowSequence.current}`, description: "", project_code: "", country: "", quantity: 1, rate: 0, total: 0 }]);
      }
    }
    if (nextMode === "manual") return;
    const realOptions = next.filter((option) => option.value !== "manual");
    if (!realOptions.length) {
      stopSourceRequest();
      resetSource();
      setOptions(allOptions);
      return;
    }
    await loadSourceDetails(realOptions.map((invoice) => Number(invoice.value || invoice.id)));
  };

  const chooseManualClient = (option) => {
    if (isEdit || savingRef.current || !canField("client") || sourceModeRef.current !== "manual") return;
    const entry = manualClients.find((item) => Number(item.id || item.value) === Number(option?.value));
    setClient(entry?.client || null);
    setCompany(entry?.invoicing_company || null);
    setSnapshot(entry?.snapshot || null);
    setSnapshotAddresses(entry?.snapshot || {});
  };
  const addManualItem = () => {
    if (!isManual || savingRef.current || !canField("add_remove_items")) return;
    rowSequence.current += 1;
    setItems((current) => [...current, { row_key: `manual-${rowSequence.current}`, description: "", project_code: "", country: "", quantity: 1, rate: 0, total: 0 }]);
  };

  const setAddress = (type, key, value) => {
    setForm((current) => ({ ...current, [type]: { ...current[type], [key]: value } }));
  };
  const toggleSame = (checked) => setForm((current) => ({
    ...current,
    shipping_same_as_billing: checked,
    shipping: checked ? { ...current.billing } : current.shipping,
  }));

  const setItem = (index, field, value) => {
    if (isManual && savingRef.current) return;
    setItems((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const updated = { ...item, [field]: value };
      if (field === "quantity" || field === "rate") {
        updated.total = (isManual ? roundManualCreditMoney : round)(Number(updated.quantity || 0) * Number(updated.rate || 0));
      }
      return updated;
    }));
  };

  const manualAmounts = useMemo(() => manualCreditNoteAmounts(items, snapshot), [items, snapshot]);
  const sourceAmount = useMemo(() => isManual ? manualAmounts.source : round(items.reduce((sum, item) => sum + Number(item.total || 0), 0)), [items, isManual, manualAmounts.source]);
  const totalQuantity = useMemo(() => round(items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)), [items]);
  const exchangeRate = String(snapshot?.currency_code || "USD").toUpperCase() === "USD"
    ? 1 : Number(snapshot?.exchange_rate || 1);
  const amount = isManual ? manualAmounts.amount : round(sourceAmount * exchangeRate);
  const tax = isManual ? manualAmounts.tax : snapshot?.tax_enabled ? round(amount * Number(snapshot?.tax_percentage || 0) / 100) : 0;
  const finalAmount = isManual ? manualAmounts.total : round(amount + tax);
  const manualTotalsVisible = !isManual || ["currency_code", "exchange_rate", "tax_enabled", "tax_percentage"]
    .every((key) => Object.prototype.hasOwnProperty.call(snapshot || {}, key));

  const submit = async () => {
    if (savingRef.current || !canSave || loading || sourceLoading || loadedActorKey !== actorKey) return;
    const validation = validateCreditNoteDraft({ sourceType, manualReference, client, company, snapshot, selected, items, isEdit });
    if (validation) {
      setSnackbar({ open: true, color: "error", message: validation });
      return;
    }
    const controller = new AbortController();
    const actor = actorKey;
    saveAbort.current = controller;
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      const billing = form.billing;
      const shipping = form.shipping_same_as_billing ? billing : form.shipping;
      const payload = {
        ...creditNoteSourcePayload({ sourceType, manualReference, client, company, snapshot, items, canField }),
        credit_note_date: form.credit_note_date,
        reference_number: form.reference_number || null,
        notes: form.notes || null,
        billing_name: billing.name,
        billing_email: billing.email,
        billing_address: billing.address,
        billing_state: billing.state,
        billing_state_id: billing.state_id || null,
        billing_postal_code: billing.postal_code,
        billing_country: billing.country,
        billing_country_id: billing.country_id || null,
        shipping_same_as_billing: form.shipping_same_as_billing,
        shipping_name: shipping.name,
        shipping_email: shipping.email,
        shipping_address: shipping.address,
        shipping_state: shipping.state,
        shipping_state_id: shipping.state_id || null,
        shipping_postal_code: shipping.postal_code,
        shipping_country: shipping.country,
        shipping_country_id: shipping.country_id || null,
      };
      const result = await request(isEdit ? `/credit-notes/${id}` : "/credit-notes", {
        method: isEdit ? "PUT" : "POST",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (controller.signal.aborted || actor !== actorRef.current) return;
      navigate("/credit-notes", {
        replace: true,
        state: { message: result.message || `Credit note ${isEdit ? "updated" : "generated"} successfully.` },
      });
    } catch (requestError) {
      if (controller.signal.aborted || actor !== actorRef.current) return;
      setError(requestError.message);
      setSnackbar({ open: true, color: "error", message: requestError.message });
    } finally {
      if (saveAbort.current === controller) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={2}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <SoftTypography variant="h4" fontWeight="medium">
            {isEdit ? `Edit Credit Note${number ? ` - ${number}` : ""}` : "Generate Credit Note"}
          </SoftTypography>
          {hasModulePermission("credit_notes.pages.list.view") && (
            <SoftButton color="secondary" size="small" onClick={() => navigate("/credit-notes")}>
              <Icon>arrow_back</Icon>&nbsp; Credit Notes
            </SoftButton>
          )}
        </SoftBox>

        {loading || loadedActorKey !== actorKey ? (
          <SoftBox py={8} display="flex" justifyContent="center"><CircularProgress color="info" /></SoftBox>
        ) : (
          <Card sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "3fr 1fr" }} gap={1.25} mb={1.5}>
              {canField("source_invoice_numbers") && <SoftBox>
                <SoftTypography variant="caption">Source Invoice Numbers * (multiple allowed)</SoftTypography>
                <SoftSelect
                  placeholder="First select an invoice number"
                  options={isEdit ? options : [{ ...MANUAL_CREDIT_OPTION, isDisabled: !canCreateManual }, ...options]}
                  value={isManual ? [MANUAL_CREDIT_OPTION] : selected}
                  isDisabled={saving || (isEdit && isManual)}
                  isOptionDisabled={(option) => Boolean(option.isDisabled)}
                  isMulti
                  isSearchable
                  isLoading={sourceLoading}
                  onChange={chooseInvoices}
                />
                {isManual && <SoftBox mt={1}>
                  <SoftTypography variant="caption">Manual Reference *</SoftTypography>
                  <SoftInput value={manualReference} placeholder="e.g. MAN-2026-0001" disabled={isEdit || saving}
                    inputProps={{ maxLength: 100, "aria-label": "Manual Reference", disabled: isEdit || saving, readOnly: isEdit || saving }}
                    onChange={(event) => { if (!isEdit && !savingRef.current) setManualReference(event.target.value); }}
                    onBlur={() => { if (!isEdit && !savingRef.current) setManualReference((current) => normalizeManualReference(current)); }} />
                  <SoftTypography variant="caption" display="block">Use 3–100 letters, numbers, dots, hyphens, underscores or slashes. Unique references cannot be changed or reused after saving.</SoftTypography>
                </SoftBox>}
                {!isManual && client && <SoftTypography variant="caption" display="block" mt={0.4}>
                  Only compatible invoices for {client.name} are shown after the first selection.
                </SoftTypography>}
              </SoftBox>}
              {canField("credit_note_date") && <SoftBox>
                <SoftTypography variant="caption">Credit Note Date *</SoftTypography>
                <SoftInput type="date" value={form.credit_note_date} inputProps={{ min: minimumDate, max: maximumDate }} onChange={(event) => setForm((current) => ({ ...current, credit_note_date: event.target.value }))} />
              </SoftBox>}
            </SoftBox>

            {(canField("client") || canField("credit_note_number") || canField("credit_note_month")) && (
              <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "2fr 1fr 1fr" }} gap={1.25} mb={1.5}>
                {canField("client") && <SoftBox><SoftTypography variant="caption">{isManual ? "Client *" : "Client (auto-selected)"}</SoftTypography>
                  {isManual && !isEdit ? <SoftSelect placeholder="Select client" aria-label="Manual Client"
                    options={manualClients.map((entry) => ({ value: entry.id || entry.value, label: entry.label || entry.client?.name }))}
                    value={client ? { value: client.id, label: client.name } : null} isClearable isDisabled={saving} onChange={chooseManualClient} />
                    : <SoftInput value={client?.name || "Selected from invoice number"} disabled />}
                </SoftBox>}
                {canField("credit_note_number") && <SoftBox><SoftTypography variant="caption">Credit Note Number</SoftTypography><SoftInput value={number || "Generated automatically"} disabled /></SoftBox>}
                {canField("credit_note_month") && <SoftBox><SoftTypography variant="caption">Credit Note Year-Month</SoftTypography><SoftInput value={(form.credit_note_date || "").slice(0, 7)} disabled /></SoftBox>}
              </SoftBox>
            )}

            {isManual && snapshot && <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "2fr 1fr 1fr" }} gap={1.25} mb={1.5}>
              {canField("billing_currency") && <SoftBox><SoftTypography variant="caption">Billing Currency *</SoftTypography>
                <SoftSelect options={currencies} aria-label="Billing Currency" isDisabled={saving}
                  value={currencies.find((entry) => entry.code === snapshot.currency_code) || { value: snapshot.currency_code, label: snapshot.currency_code }}
                  onChange={(option) => setSnapshot((current) => ({ ...current, currency_code: option?.code || option?.value || "USD", currency_symbol: option?.symbol || option?.code, exchange_rate: 1 }))} />
              </SoftBox>}
              {canField("exchange_rate") && <SoftBox><SoftTypography variant="caption">Exchange Rate (1 USD) *</SoftTypography>
                <SoftInput type="number" value={String(snapshot.currency_code || "USD").toUpperCase() === "USD" ? 1 : snapshot.exchange_rate}
                  disabled={saving || String(snapshot.currency_code || "USD").toUpperCase() === "USD"}
                  inputProps={{ min: 0.000001, step: "any", "aria-label": "Manual Exchange Rate",
                    disabled: saving || String(snapshot.currency_code || "USD").toUpperCase() === "USD",
                    readOnly: saving || String(snapshot.currency_code || "USD").toUpperCase() === "USD" }}
                  onChange={(event) => { if (!savingRef.current && String(snapshot.currency_code || "USD").toUpperCase() !== "USD") setSnapshot((current) => ({ ...current, exchange_rate: event.target.value })); }} />
              </SoftBox>}
              {canField("tax") && <SoftBox><FormControlLabel control={<Checkbox checked={Boolean(snapshot.tax_enabled)} disabled={saving}
                onChange={(event) => setSnapshot((current) => ({ ...current, tax_enabled: event.target.checked }))} />} label="Add Tax" />
                <SoftTypography variant="caption" display="block">Client/company tax rule: {Number(snapshot.tax_percentage || 0)}%</SoftTypography>
              </SoftBox>}
            </SoftBox>}

            {(company || snapshot) && <SoftBox display="flex" flexWrap="wrap" gap={2} mb={2} p={1.25} sx={{ backgroundColor: "#f8fafc", borderRadius: "8px" }}>
              {canField("invoicing_company") && <SoftTypography variant="button"><strong>Invoicing Company:</strong> {company?.name || "-"} ({company?.country || "-"})</SoftTypography>}
              {canField("billing_currency") && <SoftTypography variant="button"><strong>Billing Currency:</strong> {snapshot?.currency_code || "USD"}</SoftTypography>}
              {canField("exchange_rate") && <SoftTypography variant="button"><strong>Exchange Rate:</strong> {String(snapshot?.currency_code || "USD").toUpperCase() === "USD" ? "N/A" : `1 USD = ${snapshot?.exchange_rate}`}</SoftTypography>}
            </SoftBox>}

            <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mb={2}>
              <SoftBox>
                <SoftTypography variant="h6" mb={0.75}>Credit To Client</SoftTypography>
                {addressFields.filter(([key]) => !["state", "country"].includes(key) && canField(`billing_${key}`)).map(([key, label]) => <SoftBox key={key} mb={0.7}>
                  <SoftTypography variant="caption">{label}</SoftTypography>
                  <SoftInput value={form.billing[key] || ""} onChange={(event) => setAddress("billing", key, event.target.value)} />
                </SoftBox>)}
                <MasterLocationSelect
                  countryId={form.billing.country_id}
                  countryName={form.billing.country}
                  stateId={form.billing.state_id}
                  stateName={form.billing.state}
                  showCountry={canField("billing_country")}
                  showState={canField("billing_state")}
                  onCountryChange={({ id: countryId, name }) => setForm((current) => ({
                    ...current,
                    billing: {
                      ...current.billing,
                      country_id: countryId,
                      country: name,
                      state_id: null,
                      state: "",
                    },
                  }))}
                  onStateChange={({ id: stateId, name }) => setForm((current) => ({
                    ...current,
                    billing: { ...current.billing, state_id: stateId, state: name },
                  }))}
                />
              </SoftBox>
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.4}>
                  <SoftTypography variant="h6">Shipping To Client</SoftTypography>
                  {canField("shipping_same_as_billing") && <FormControlLabel control={<Checkbox checked={form.shipping_same_as_billing} onChange={(event) => toggleSame(event.target.checked)} />} label="Same as Billing" />}
                </SoftBox>
                {addressFields.filter(([key]) => !["state", "country"].includes(key) && canField(`shipping_${key}`)).map(([key, label]) => <SoftBox key={key} mb={0.7}>
                  <SoftTypography variant="caption">{label}</SoftTypography>
                  <SoftInput value={(form.shipping_same_as_billing ? form.billing : form.shipping)[key] || ""} disabled={form.shipping_same_as_billing} onChange={(event) => setAddress("shipping", key, event.target.value)} />
                </SoftBox>)}
                <MasterLocationSelect
                  countryId={(form.shipping_same_as_billing ? form.billing : form.shipping).country_id}
                  countryName={(form.shipping_same_as_billing ? form.billing : form.shipping).country}
                  stateId={(form.shipping_same_as_billing ? form.billing : form.shipping).state_id}
                  stateName={(form.shipping_same_as_billing ? form.billing : form.shipping).state}
                  disabled={form.shipping_same_as_billing}
                  showCountry={canField("shipping_country")}
                  showState={canField("shipping_state")}
                  onCountryChange={({ id: countryId, name }) => setForm((current) => ({
                    ...current,
                    shipping: {
                      ...current.shipping,
                      country_id: countryId,
                      country: name,
                      state_id: null,
                      state: "",
                    },
                  }))}
                  onStateChange={({ id: stateId, name }) => setForm((current) => ({
                    ...current,
                    shipping: { ...current.shipping, state_id: stateId, state: name },
                  }))}
                />
              </SoftBox>
            </SoftBox>

            {canField("items_section") && <SoftBox mb={2}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                <SoftTypography variant="h6">{isManual ? "Manual Credit Rows" : "Source Invoice Credit Rows"}</SoftTypography>
                {isManual && canField("add_remove_items") && <SoftButton size="small" color="info" disabled={saving} onClick={addManualItem}><Icon>add</Icon>&nbsp; Add Item</SoftButton>}
              </SoftBox>
              {sourceLoading ? <SoftBox py={3} display="flex" justifyContent="center"><CircularProgress size={24} color="info" /></SoftBox> : (
                <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 950, tableLayout: "fixed" }}>
                    <TableHead sx={{ display: "table-header-group" }}><TableRow>
                      {canField("item_source_invoice") && <TableCell sx={headSx}>Source Invoice</TableCell>}
                      {canField("item_project_code") && <TableCell sx={headSx}>Project Code</TableCell>}
                      {canField("item_country") && <TableCell sx={headSx}>Country</TableCell>}
                      {canField("item_description") && <TableCell sx={{ ...headSx, width: "25%" }}>Description</TableCell>}
                      {canField("item_quantity") && <TableCell sx={headSx}>Qty</TableCell>}
                      {canField("item_rate") && <TableCell sx={headSx}>Rate (USD)</TableCell>}
                      {canField("item_amount") && <TableCell sx={headSx}>Credit (USD)</TableCell>}
                      {canField("add_remove_items") && <TableCell sx={headSx}>Action</TableCell>}
                    </TableRow></TableHead>
                    <TableBody>
                      {items.map((item, index) => <TableRow key={isManual ? item.row_key || item.id : item.source_invoice_item_id}>
                        {canField("item_source_invoice") && <TableCell sx={cellSx}>{isManual ? "Manual" : item.source_invoice_number}</TableCell>}
                        {canField("item_project_code") && <TableCell sx={cellSx}>{isManual ? <SoftInput value={item.project_code || ""} inputProps={{ "aria-label": `Item ${index + 1} Project Code`, disabled: saving, readOnly: saving }} onChange={(event) => setItem(index, "project_code", event.target.value)} /> : item.project_code || "-"}</TableCell>}
                        {canField("item_country") && <TableCell sx={cellSx}>{isManual ? <SoftInput value={item.country || ""} inputProps={{ "aria-label": `Item ${index + 1} Country`, disabled: saving, readOnly: saving }} onChange={(event) => setItem(index, "country", event.target.value)} /> : item.country || "-"}</TableCell>}
                        {canField("item_description") && <TableCell sx={cellSx}><SoftInput value={item.description || ""} inputProps={{ "aria-label": `Item ${index + 1} Description`, disabled: isManual && saving, readOnly: isManual && saving }} onChange={(event) => setItem(index, "description", event.target.value)} /></TableCell>}
                        {canField("item_quantity") && <TableCell sx={cellSx}><SoftInput type="number" value={item.quantity} inputProps={{ min: 0.01, max: isManual ? undefined : Number(item.remaining_quantity || 0), step: 0.01, "aria-label": `Item ${index + 1} Quantity`, disabled: isManual && saving, readOnly: isManual && saving }} onChange={(event) => setItem(index, "quantity", event.target.value)} /></TableCell>}
                        {canField("item_rate") && <TableCell sx={cellSx}><SoftInput type="number" value={item.rate} inputProps={{ min: 0, step: 0.01, "aria-label": `Item ${index + 1} Rate`, disabled: isManual && saving, readOnly: isManual && saving }} onChange={(event) => setItem(index, "rate", event.target.value)} /></TableCell>}
                        {canField("item_amount") && <TableCell sx={cellSx}>
                          <SoftInput type="number" value={item.total} inputProps={{ min: 0.01, max: isManual ? undefined : item.remaining_total, step: 0.01, "aria-label": `Item ${index + 1} Amount`, disabled: isManual && saving, readOnly: isManual && saving }} onChange={(event) => setItem(index, "total", event.target.value)} />
                          {!isManual && <SoftTypography variant="caption">Max ${money(item.remaining_total)}</SoftTypography>}
                        </TableCell>}
                        {canField("add_remove_items") && <TableCell sx={cellSx} align="center"><IconButton color="error" size="small" onClick={() => setItems((current) => current.filter((_, row) => row !== index))}><Icon>delete</Icon></IconButton></TableCell>}
                      </TableRow>)}
                      {!items.length && <TableRow><TableCell colSpan={8} align="center">{isManual ? "Select a client, then add manual credit rows." : "Select source invoice numbers to load credit rows."}</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <SoftBox display="flex" justifyContent="flex-end" flexWrap="wrap" gap={3} mt={1}>
                {canField("total_quantity") && <SoftTypography variant="button" fontWeight="bold">Total Qty: {money(totalQuantity)}</SoftTypography>}
                {canField("usd_amount") && <SoftTypography variant="button" color="info" fontWeight="bold">Credit Amount: ${money(sourceAmount)} USD</SoftTypography>}
              </SoftBox>
            </SoftBox>}

            {(canField("tax") || canField("final_amount")) && <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 380px" }} gap={2} mb={2} p={1.5} sx={{ border: "1px solid #e5e7eb", borderRadius: "8px" }}>
              <SoftBox>
                <SoftTypography variant="h6">{isManual ? "Manual Currency & Tax" : "Copied Invoice Currency & Tax Snapshot"}</SoftTypography>
                <SoftTypography variant="button" display="block">{isManual ? "Tax follows the selected client/company settings. This note does not consume any invoice balance." : "Currency, exchange rate and tax rule are copied from the selected source invoice(s)."}</SoftTypography>
                {canField("notes") && <SoftBox mt={1}><SoftTypography variant="caption">Notes</SoftTypography><SoftInput multiline rows={3} value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} /></SoftBox>}
              </SoftBox>
              <SoftBox display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="center">
                <SoftTypography variant="button" textAlign="right">USD Credit:</SoftTypography><SoftInput value={`$${money(sourceAmount)} USD`} disabled />
                <SoftTypography variant="button" textAlign="right">Amount:</SoftTypography><SoftInput value={manualTotalsVisible ? `${snapshot?.currency_symbol || snapshot?.currency_code || "$"}${money(amount)}` : "Calculated on save"} disabled />
                <SoftTypography variant="button" textAlign="right">Tax{manualTotalsVisible ? ` (${Number(snapshot?.tax_percentage || 0)}%)` : ""}:</SoftTypography><SoftInput value={manualTotalsVisible ? `${snapshot?.currency_symbol || snapshot?.currency_code || "$"}${money(tax)}` : "Calculated on save"} disabled />
                <SoftTypography variant="button" textAlign="right" fontWeight="bold">Final Credit:</SoftTypography><SoftInput value={manualTotalsVisible ? `${snapshot?.currency_symbol || snapshot?.currency_code || "$"}${money(finalAmount)}` : "Calculated on save"} disabled />
              </SoftBox>
            </SoftBox>}

            {error && <SoftTypography color="error" variant="button" display="block" mb={1}>{error}</SoftTypography>}
            {canSave && <SoftButton color="info" fullWidth disabled={saving || sourceLoading || !items.length || !form.credit_note_date} onClick={submit}>
              {saving ? "Saving..." : isEdit ? "Update Credit Note" : "Generate Credit Note"}
            </SoftButton>}
          </Card>
        )}
      </SoftBox>
      <Footer />
      <SoftSnackbar color={snackbar.color} icon={snackbar.color === "success" ? "check" : "warning"} title="Credit Note" content={snackbar.message} open={snackbar.open} onClose={() => setSnackbar((current) => ({ ...current, open: false }))} close={() => setSnackbar((current) => ({ ...current, open: false }))} />
    </DashboardLayout>
  );
}

CreditNoteForm.propTypes = { mode: PropTypes.oneOf(["create", "edit"]).isRequired };

export default CreditNoteForm;
